'use client'

import React, { forwardRef, useState } from 'react'
import styled from 'styled-components'
import { $DCprimaryColor, colors } from '../../styleGuide'

interface InputProps {
  type?: string
  placeholder?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  name?: string
  id?: string
  autoComplete?: string
  autoFocus?: boolean
  maxLength?: number
  minLength?: number
  pattern?: string
  step?: number
  min?: number
  max?: number
  multiple?: boolean
  accept?: string
  capture?: string
  form?: string
  formAction?: string
  formEncType?: string
  formMethod?: string
  formNoValidate?: boolean
  formTarget?: string
  height?: string
  width?: string
  list?: string
  dirName?: string
  alt?: string
  src?: string
  useMap?: string
  isShowError?: boolean
  errorMessage?: string
  label?: string
  helperText?: string
  isRequired?: boolean
  isShowLabel?: boolean
  isShowHelperText?: boolean
  isShowErrorMessage?: boolean
  isShowIcon?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  iconClick?: () => void
  isShowClearButton?: boolean
  clearButtonClick?: () => void
  isShowPasswordToggle?: boolean
  passwordToggleClick?: () => void
  isShowPassword?: boolean
  isShowLoading?: boolean
  loadingText?: string
  isShowSuccess?: boolean
  successText?: string
  isShowWarning?: boolean
  warningText?: string
  isShowInfo?: boolean
  infoText?: string
  variant?: 'default' | 'outlined' | 'filled' | 'standard'
  size?: 'small' | 'medium' | 'large'
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  fullWidth?: boolean
  margin?: string
  padding?: string
  borderRadius?: string
  borderWidth?: string
  borderStyle?: string
  borderColor?: string
  backgroundColor?: string
  fontSize?: string
  fontWeight?: string
  fontFamily?: string
  textAlign?: string
  textDecoration?: string
  textTransform?: string
  letterSpacing?: string
  lineHeight?: string
  whiteSpace?: string
  overflow?: string
  textOverflow?: string
  wordBreak?: string
  wordWrap?: string
  hyphens?: string
  direction?: string
  unicodeBidi?: string
  writingMode?: string
  textOrientation?: string
  tabSize?: string
  caretColor?: string
  userSelect?: string
  pointerEvents?: string
  cursor?: string
  resize?: string
  outline?: string
  outlineOffset?: string
  outlineStyle?: string
  outlineWidth?: string
  outlineColor?: string
  boxShadow?: string
  filter?: string
  backdropFilter?: string
  opacity?: string
  mixBlendMode?: string
  isolation?: string
  zIndex?: string
  position?: string
  top?: string
  right?: string
  bottom?: string
  left?: string
  transform?: string
  transformOrigin?: string
  transformStyle?: string
  perspective?: string
  perspectiveOrigin?: string
  backfaceVisibility?: string
  visibility?: string
  clip?: string
  clipPath?: string
  mask?: string
  maskImage?: string
  maskMode?: string
  maskRepeat?: string
  maskPosition?: string
  maskSize?: string
  maskOrigin?: string
  maskClip?: string
  maskComposite?: string
  maskBorder?: string
  maskBorderSource?: string
  maskBorderMode?: string
  maskBorderSlice?: string
  maskBorderWidth?: string
  maskBorderOutset?: string
  maskBorderRepeat?: string
  maskType?: string
  paintOrder?: string
  shapeRendering?: string
  textRendering?: string
  imageRendering?: string
  colorInterpolation?: string
  colorInterpolationFilters?: string
  colorProfile?: string
  colorRendering?: string
  fill?: string
  fillOpacity?: string
  fillRule?: string
  stroke?: string
  strokeDasharray?: string
  strokeDashoffset?: string
  strokeLinecap?: string
  strokeLinejoin?: string
  strokeMiterlimit?: string
  strokeOpacity?: string
  strokeWidth?: string
  alignmentBaseline?: string
  baselineShift?: string
  dominantBaseline?: string
  fontFeatureSettings?: string
  fontKerning?: string
  fontLanguageOverride?: string
  fontOpticalSizing?: string
  fontSynthesis?: string
  fontVariantAlternates?: string
  fontVariantCaps?: string
  fontVariantEastAsian?: string
  fontVariantLigatures?: string
  fontVariantNumeric?: string
  fontVariantPosition?: string
  fontVariationSettings?: string
  textAnchor?: string
  vectorEffect?: string
  clipRule?: string
  floodColor?: string
  floodOpacity?: string
  lightingColor?: string
  stopColor?: string
  stopOpacity?: string
}

const InputContainer = styled.div<{ isShowError: boolean }>`
  position: relative;
  width: 100%;

  .error-message {
    color: ${colors.danger};
    font-size: 12px;
    margin-top: 4px;
    display: ${(props) => (props.isShowError ? 'block' : 'none')};
  }

  .error-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.danger};
    font-size: 16px;
  }

  .error-border {
    border-top: 5px solid ${colors.danger};
  }
`

const StyledInput = styled.input<{ isShowError: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid
    ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s ease-in-out;
  ${(props) =>
    props.isShowError && `box-shadow: 0 0 0 1px ${colors.danger} !important;`}

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.isShowError ? colors.danger : $DCprimaryColor};
    box-shadow: 0 0 0 1px
      ${(props) => (props.isShowError ? colors.dangerLight : $DCprimaryColor)};
  }

  &:disabled {
    background-color: ${colors.grayLighter} !important;
    border-color: ${colors.grayInputBorder} !important;
    color: ${colors.grayMedium};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.grayMedium};
  }

  &.error {
    background-color: ${colors.dangerLight} !important;
    border-color: ${colors.danger} !important;
  }

  &.success {
    border: 0.0625rem solid
      ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
    color: ${colors.grayInputDark};
  }

  &.warning {
    border-color: ${colors.warning};
  }

  &.info {
    border-color: ${colors.info};
  }

  &.default {
    border: 0.0625rem solid
      ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
    color: ${colors.grayInputDark};
  }

  &.outlined {
    border: 2px solid
      ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
    background-color: transparent;
  }

  &.filled {
    border: none;
    border-bottom: 2px solid
      ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
    background-color: ${(props) =>
      props.isShowError ? colors.dangerLight : colors.white};
    border-radius: 0;
  }

  &.standard {
    border: none;
    border-bottom: 1px solid
      ${(props) => (props.isShowError ? colors.danger : colors.grayInputBorder)};
    background-color: transparent;
    border-radius: 0;
  }

  &.small {
    padding: 5px 8px;
    font-size: 12px;
  }

  &.medium {
    padding: 10px 12px;
    font-size: 14px;
  }

  &.large {
    padding: 14px 16px;
    font-size: 16px;
  }

  &.primary {
    border-color: ${colors.primary};
  }

  &.secondary {
    border-color: ${colors.secondary};
  }

  &.success {
    border-color: ${colors.success};
  }

  &.error {
    border-color: ${colors.danger};
  }

  &.warning {
    border-color: ${colors.warning};
  }

  &.info {
    border-color: ${colors.info};
  }

  &.full-width {
    width: 100%;
  }
`

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      placeholder,
      value,
      onChange,
      onBlur,
      onFocus,
      className,
      disabled = false,
      readOnly = false,
      required = false,
      name,
      id,
      autoComplete,
      autoFocus = false,
      maxLength,
      minLength,
      pattern,
      size,
      step,
      min,
      max,
      multiple = false,
      accept,
      capture,
      form,
      formAction,
      formEncType,
      formMethod,
      formNoValidate = false,
      formTarget,
      height,
      width,
      list,
      dirName,
      alt,
      src,
      useMap,
      isShowError = false,
      errorMessage,
      label,
      helperText,
      isRequired = false,
      isShowLabel = true,
      isShowHelperText = true,
      isShowErrorMessage = true,
      isShowLoading = false,
      loadingText = 'Loading...',
      isShowSuccess = false,
      successText,
      isShowWarning = false,
      warningText,
      isShowInfo = false,
      infoText,
      variant = 'default',
      size: inputSize = 'medium',
      color: inputColor = 'primary',
      fullWidth = false,
      margin,
      padding,
      borderRadius,
      borderWidth,
      borderStyle,
      borderColor,
      backgroundColor,
      color: textColor,
      fontSize,
      fontWeight,
      fontFamily,
      textAlign,
      textDecoration,
      textTransform,
      letterSpacing,
      lineHeight,
      whiteSpace,
      overflow,
      textOverflow,
      wordBreak,
      wordWrap,
      hyphens,
      direction,
      unicodeBidi,
      writingMode,
      textOrientation,
      tabSize,
      caretColor,
      userSelect,
      pointerEvents,
      cursor,
      resize,
      outline,
      outlineOffset,
      outlineStyle,
      outlineWidth,
      outlineColor,
      boxShadow,
      filter,
      backdropFilter,
      opacity,
      mixBlendMode,
      isolation,
      zIndex,
      position,
      top,
      right,
      bottom,
      left,
      transform,
      transformOrigin,
      transformStyle,
      perspective,
      perspectiveOrigin,
      backfaceVisibility,
      visibility,
      clip,
      clipPath,
      mask,
      maskImage,
      maskMode,
      maskRepeat,
      maskPosition,
      maskSize,
      maskOrigin,
      maskClip,
      maskComposite,
      maskBorder,
      maskBorderSource,
      maskBorderMode,
      maskBorderSlice,
      maskBorderWidth,
      maskBorderOutset,
      maskBorderRepeat,
      maskType,
      paintOrder,
      shapeRendering,
      textRendering,
      imageRendering,
      colorInterpolation,
      colorInterpolationFilters,
      colorProfile,
      colorRendering,
      fill,
      fillOpacity,
      fillRule,
      stroke,
      strokeDasharray,
      strokeDashoffset,
      strokeLinecap,
      strokeLinejoin,
      strokeMiterlimit,
      strokeOpacity,
      strokeWidth,
      alignmentBaseline,
      baselineShift,
      dominantBaseline,
      fontFeatureSettings,
      fontKerning,
      fontLanguageOverride,
      fontOpticalSizing,
      fontSynthesis,
      fontVariantAlternates,
      fontVariantCaps,
      fontVariantEastAsian,
      fontVariantLigatures,
      fontVariantNumeric,
      fontVariantPosition,
      fontVariationSettings,
      textAnchor,
      vectorEffect,
      clipRule,
      floodColor,
      floodOpacity,
      lightingColor,
      stopColor,
      stopOpacity,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    return (
      <InputContainer isShowError={isShowError}>
        {isShowLabel && label && (
          <label htmlFor={id}>
            {label}
            {isRequired && <span style={{ color: colors.danger }}>*</span>}
          </label>
        )}
        <StyledInput
          ref={ref}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`${
            className || ''
          } ${variant} ${inputSize} ${inputColor} ${
            fullWidth ? 'full-width' : ''
          } ${isShowError ? 'error' : ''}`}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          name={name}
          id={id}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          maxLength={maxLength}
          minLength={minLength}
          pattern={pattern}
          size={size}
          step={step}
          min={min}
          max={max}
          multiple={multiple}
          accept={accept}
          capture={capture}
          form={form}
          formAction={formAction}
          formEncType={formEncType}
          formMethod={formMethod}
          formNoValidate={formNoValidate}
          formTarget={formTarget}
          height={height}
          width={width}
          list={list}
          dirName={dirName}
          alt={alt}
          src={src}
          useMap={useMap}
          isShowError={isShowError}
          style={{
            margin,
            padding,
            borderRadius,
            borderWidth,
            borderStyle,
            borderColor,
            backgroundColor,
            color: textColor,
            fontSize,
            fontWeight,
            fontFamily,
            textAlign,
            textDecoration,
            textTransform,
            letterSpacing,
            lineHeight,
            whiteSpace,
            overflow,
            textOverflow,
            wordBreak,
            wordWrap,
            hyphens,
            direction,
            unicodeBidi,
            writingMode,
            textOrientation,
            tabSize,
            caretColor,
            userSelect,
            pointerEvents,
            cursor,
            resize,
            outline,
            outlineOffset,
            outlineStyle,
            outlineWidth,
            outlineColor,
            boxShadow,
            filter,
            backdropFilter,
            opacity,
            mixBlendMode,
            isolation,
            zIndex,
            position,
            top,
            right,
            bottom,
            left,
            transform,
            transformOrigin,
            transformStyle,
            perspective,
            perspectiveOrigin,
            backfaceVisibility,
            visibility,
            clip,
            clipPath,
            mask,
            maskImage,
            maskMode,
            maskRepeat,
            maskPosition,
            maskSize,
            maskOrigin,
            maskClip,
            maskComposite,
            maskBorder,
            maskBorderSource,
            maskBorderMode,
            maskBorderSlice,
            maskBorderWidth,
            maskBorderOutset,
            maskBorderRepeat,
            maskType,
            paintOrder,
            shapeRendering,
            textRendering,
            imageRendering,
            colorInterpolation,
            colorInterpolationFilters,
            colorProfile,
            colorRendering,
            fill,
            fillOpacity,
            fillRule,
            stroke,
            strokeDasharray,
            strokeDashoffset,
            strokeLinecap,
            strokeLinejoin,
            strokeMiterlimit,
            strokeOpacity,
            strokeWidth,
            alignmentBaseline,
            baselineShift,
            dominantBaseline,
            fontFeatureSettings,
            fontKerning,
            fontLanguageOverride,
            fontOpticalSizing,
            fontSynthesis,
            fontVariantAlternates,
            fontVariantCaps,
            fontVariantEastAsian,
            fontVariantLigatures,
            fontVariantNumeric,
            fontVariantPosition,
            fontVariationSettings,
            textAnchor,
            vectorEffect,
            clipRule,
            floodColor,
            floodOpacity,
            lightingColor,
            stopColor,
            stopOpacity,
          }}
          {...rest}
        />
        {isShowErrorMessage && isShowError && errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}
        {isShowHelperText && helperText && (
          <div
            style={{
              color: colors.grayMedium,
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {helperText}
          </div>
        )}
        {isShowSuccess && successText && (
          <div
            style={{
              color: colors.success,
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {successText}
          </div>
        )}
        {isShowWarning && warningText && (
          <div
            style={{
              color: colors.warning,
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {warningText}
          </div>
        )}
        {isShowInfo && infoText && (
          <div
            style={{ color: colors.info, fontSize: '12px', marginTop: '4px' }}
          >
            {infoText}
          </div>
        )}
        {isShowLoading && (
          <div
            style={{
              color: colors.grayMedium,
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {loadingText}
          </div>
        )}
      </InputContainer>
    )
  },
)

Input.displayName = 'Input'

export default Input
